<template>
  <div class="game-card-wrapper">
    <div class="game-card">
      <button 
       class="collapse-btn"
       @click.stop="$emit('collapse')"
       aria-label="Collapse game details"
       type="button"
      >
        ✕
      </button>

      <div class="rank-badge" v-if="rank">
        <span class="rank-number">#{{ rank }}</span>
      </div>

      <div class="card-header">
        <img
          v-if="game.image"
          :src="formatUrl(game.image)"
          alt="Game Cover"
          class="game-cover"
          @error="handleImageError"
        />
        <div class="game-cover-placeholder" v-else>
          <!-- SVG Icon: Game Controller -->
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="placeholder-icon">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
          </svg>
        </div>

        <div class="header-info">
          <h2 class="game-title" v-html="highlightedName"></h2>
          <div class="description-container">
            <p class="game-description" v-html="computedDescription"></p>
            <button 
              v-if="isDescriptionLong" 
              @click.stop="toggleDescription" 
              class="read-more-btn"
            >
              {{ descriptionExpanded ? 'Show Less' : 'Read More' }}
            </button>
          </div>

          <div class="meta-row">
            <span v-if="game.release_date" class="meta-item">
              <!-- Calendar Icon -->
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="meta-icon"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>
              {{ formatDate(game.release_date) }}
            </span>
            <span v-if="game.metacritic_score > 0" class="meta-item score">
              <!-- Star Icon -->
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="meta-icon"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              {{ game.metacritic_score }}
            </span>
            <span v-if="hasReviews" class="meta-item">
              <!-- Thumbs Up Icon -->
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="meta-icon"><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"/></svg>
              {{ reviewRatio }}
            </span>
            <span class="meta-item price-badge" :class="{'discounted': primaryPrice && primaryPrice.is_currently_discounted}">
              <!-- Tag Icon -->
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="meta-icon"><path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l7.956 7.956a2.3 2.3 0 0 0 3.251 0l6.586-6.586a2.3 2.3 0 0 0 0-3.251l-7.956-7.956Zm-5.328 5.328a1.5 1.5 0 1 1-2.12-2.122 1.5 1.5 0 0 1 2.12 2.122Z"/></svg>
              <span>{{ displayPriceText }}</span>
              <span v-if="primaryPrice && primaryPrice.is_currently_discounted" class="discount-percent">-{{ primaryPrice.discount_percent }}%</span>
            </span>
          </div>

          <div v-if="displayReasons.length" class="why-section">
            <h3 class="why-title">Why this matched</h3>
            <div class="why-tags">
              <span
                v-for="reason in displayReasons"
                :key="reason"
                class="why-chip"
              >
                {{ reason }}
              </span>
            </div>
          </div>

          <details v-if="hasScoreMath" class="score-math-panel">
            <summary>Score snapshot</summary>
            <p class="score-math-caption">Main ranking signals behind this result.</p>
            <div v-if="scoreMathItems.length" class="score-math-grid">
              <div
                v-for="item in scoreMathItems"
                :key="item.label"
                class="score-math-item"
              >
                <span class="score-math-label">{{ item.label }}</span>
                <span class="score-math-value">{{ item.value }}</span>
                <span class="score-math-description">{{ item.description }}</span>
              </div>
            </div>
          </details>

          <!-- Like and Dislike Buttons -->
          <div class="feedback-section">
            <button @click.stop="handleFeedback('like')" class="feedback-button like-button" aria-label="Like this game">
              <!-- Thumbs Up -->
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon"><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"/></svg>
              <span>Like</span>
            </button>
            <button @click.stop="handleFeedback('dislike')" class="feedback-button dislike-button" aria-label="Dislike this game">
              <!-- Thumbs Down -->
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon"><path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z"/></svg>
              <span>Dislike</span>
            </button>
          </div>
        </div>
      </div>

      <div class="card-body">
        <!-- Links (Moved to top) -->
        <div v-if="storeLinks.length" class="links-section-top">
          <a
            v-for="link in storeLinks"
            :key="link.url"
            href="#"
            @click.stop.prevent="handleLinkClick(link)"
            class="store-link-compact"
          >
            <!-- Store Icon Logic (Simplified text/SVG for now, or just text) -->
            <span class="link-icon">
                <svg v-if="link.store === 'Steam'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 64 64">
                   <path d="M1.305 41.202C5.259 54.386 17.488 64 31.959 64c17.673 0 32-14.327 32-32s-14.327-32-32-32C15.001 0 1.124 13.193.028 29.874c2.074 3.477 2.879 5.628 1.275 11.328z" fill="#000"/>
                   <path d="M30.31 23.985l.003.158-7.83 11.375c-1.268-.058-2.54.165-3.748.662a8.14 8.14 0 0 0-1.498.8L.042 29.893s-.398 6.546 1.26 11.424l12.156 5.016c.6 2.728 2.48 5.12 5.242 6.27a8.88 8.88 0 0 0 11.603-4.782 8.89 8.89 0 0 0 .684-3.656L42.18 36.16l.275.005c6.705 0 12.155-5.466 12.155-12.18s-5.44-12.16-12.155-12.174c-6.702 0-12.155 5.46-12.155 12.174zm-1.88 23.05c-1.454 3.5-5.466 5.147-8.953 3.694a6.84 6.84 0 0 1-3.524-3.362l3.957 1.64a5.04 5.04 0 0 0 6.591-2.719 5.05 5.05 0 0 0-2.715-6.601l-4.1-1.695c1.578-.6 3.372-.62 5.05.077 1.7.703 3 2.027 3.696 3.72s.692 3.56-.01 5.246M42.466 32.1a8.12 8.12 0 0 1-8.098-8.113 8.12 8.12 0 0 1 8.098-8.111 8.12 8.12 0 0 1 8.1 8.111 8.12 8.12 0 0 1-8.1 8.113m-6.068-8.126a6.09 6.09 0 0 1 6.08-6.095c3.355 0 6.084 2.73 6.084 6.095a6.09 6.09 0 0 1-6.084 6.093 6.09 6.09 0 0 1-6.081-6.093z" fill="#fff"/>
                </svg>
                <!-- AUTHENTIC GOG LOGO (USER PROVIDED PATHS) -->
                <svg v-else-if="link.store === 'GOG'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 74 74">
                    <path d="M73.783 66.252c0 1.075-.433 2.04-1.137 2.745a3.864 3.864 0 0 1-2.743 1.135H5.483a3.876 3.876 0 0 1-3.88-3.88V5.482A3.872 3.872 0 0 1 2.74 2.738a3.868 3.868 0 0 1 2.743-1.136h64.42a3.87 3.87 0 0 1 2.743 1.136 3.861 3.861 0 0 1 1.137 2.742zm-.005-64.647A5.459 5.459 0 0 0 69.903 0H5.483a5.459 5.459 0 0 0-3.875 1.605A5.461 5.461 0 0 0 0 5.481v60.77c0 1.513.615 2.889 1.608 3.878a5.467 5.467 0 0 0 3.875 1.607h64.42a5.467 5.467 0 0 0 3.875-1.607 5.467 5.467 0 0 0 1.608-3.877V5.482a5.467 5.467 0 0 0-1.608-3.877" fill="#000"/>
                    <path d="M38.734 47.308v6.257a1.244 1.244 0 0 1-1.238 1.25c-.006 0-.008-.007-.014-.007v.007h-6.256v-.007s-.007.007-.014.007a1.244 1.244 0 0 1-1.24-1.25V47.3c0-.688.552-1.247 1.24-1.247h6.284a1.24 1.24 0 0 1 1.238 1.247zm.937-5.421H29.036A3.23 3.23 0 0 0 25.8 45.11v10.64a3.233 3.233 0 0 0 3.236 3.233h10.635a3.233 3.233 0 0 0 3.232-3.233V45.11a3.232 3.232 0 0 0-3.232-3.224" fill="#fff"/>
                    <path d="M42.13 18.248v6.353a1.25 1.25 0 0 1-1.248 1.267h-6.375a1.261 1.261 0 0 1-1.26-1.267v-6.364c0-.697.56-1.261 1.26-1.261h6.375a1.248 1.248 0 0 1 1.248 1.261zm.96-5.505H32.295a3.28 3.28 0 0 0-3.279 3.282v10.792a3.277 3.277 0 0 0 3.279 3.282H43.09a3.283 3.283 0 0 0 3.28-3.282V16.025a3.284 3.284 0 0 0-3.28-3.282" fill="#fff"/>
                    <path d="M67.112 34.433a3.28 3.28 0 0 1-3.281 3.284H49.755v-4.23h11.867c.7 0 1.264-.571 1.264-1.266V18.237a1.261 1.261 0 0 0-1.264-1.262h-6.376a1.26 1.26 0 0 0-1.255 1.262v6.364c0 .699.564 1.266 1.255 1.266h5.092v4.23h-7.3a3.28 3.28 0 0 1-3.283-3.281V16.024a3.281 3.281 0 0 1 3.284-3.283H63.83a3.284 3.284 0 0 1 3.281 3.283z" fill="#fff"/>
                    <path d="M67.102 58.985H62.93v-12.93H60a1.241 1.241 0 0 0-1.242 1.246v11.684h-4.17v-12.93h-2.933a1.243 1.243 0 0 0-1.242 1.246v11.695h-4.17V45.112a3.23 3.23 0 0 1 3.235-3.224h17.623z" fill="#fff"/>
                    <path d="M25.628 34.433a3.28 3.28 0 0 1-3.282 3.284H8.27v-4.23h11.867c.698 0 1.258-.571 1.258-1.266V18.237c0-.697-.56-1.262-1.258-1.262H13.76a1.259 1.259 0 0 0-1.258 1.262v6.364a1.26 1.26 0 0 0 1.258 1.266h5.095v4.23h-7.303a3.277 3.277 0 0 1-3.281-3.281V16.024a3.28 3.28 0 0 1 3.281-3.283h10.794a3.283 3.283 0 0 1 3.282 3.283z" fill="#fff"/>
                    <path d="M22.462 46.053h-8.77a1.241 1.241 0 0 0-1.24 1.247s.003 0 .003.008h-.003v6.257h.003l-.003.005c0 .686.552 1.25 1.24 1.25h8.77v4.174H11.519v-.01A3.235 3.235 0 0 1 8.28 55.75V45.11a3.233 3.233 0 0 1 3.239-3.223h10.943z" fill="#fff"/>
                </svg>
                <span v-else>🔗</span>
            </span>
            <span class="link-text">{{ link.store || 'Store' }}</span>
            <span class="external-icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" style="width:12px;height:12px">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
                </svg>
            </span>
          </a>
        </div>
        <div class="info-grid">
          <div v-if="game.developer && game.developer.length" class="info-item">
            <span class="info-label">Developer</span>
            <span class="info-value">{{ game.developer.join(', ') }}</span>
          </div>
          <div v-if="game.publisher && game.publisher.length" class="info-item">
            <span class="info-label">Publisher</span>
            <span class="info-value">{{ game.publisher.join(', ') }}</span>
          </div>
        </div>
        
        <!-- How Long To Beat -->
        <div v-if="hasHltbData" class="hltb-section">
          <h3 class="section-title">
             <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="icon" style="width:18px;height:18px">
               <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
             </svg>
             Time to Beat
          </h3>
          <div class="hltb-grid">
             <div class="hltb-card" v-if="game.how_long_to_beat.main_story" title="Main Story">
                 <span class="hltb-label">Main</span>
                 <span class="hltb-value">{{ formatHltbTime(game.how_long_to_beat.main_story) }}</span>
             </div>
             <div class="hltb-card" v-if="game.how_long_to_beat.main_extra" title="Main + Extras">
                 <span class="hltb-label">Main+</span>
                 <span class="hltb-value">{{ formatHltbTime(game.how_long_to_beat.main_extra) }}</span>
             </div>
             <div class="hltb-card" v-if="game.how_long_to_beat.completionist" title="Completionist (100%)">
                 <span class="hltb-label">100%</span>
                 <span class="hltb-value">{{ formatHltbTime(game.how_long_to_beat.completionist) }}</span>
             </div>
          </div>
        </div>

        <div v-if="game.genres && game.genres.length" class="tags-section">
          <h3 class="section-title">Genres</h3>
          <div class="tags-container">
            <span
              v-for="genre in game.genres"
              :key="genre"
              class="tag genre-tag"
            >
              {{ genre }}
            </span>
          </div>
        </div>

        <div v-if="game.categories && game.categories.length" class="tags-section">
          <h3 class="section-title">Categories</h3>
          <div class="tags-container">
            <span
              v-for="category in game.categories.slice(0, 8)"
              :key="category"
              class="tag"
            >
              {{ category }}
            </span>
          </div>
        </div>

        <div v-if="game.tags && game.tags.length" class="tags-section">
          <h3 class="section-title">Tags</h3>
          <div class="tags-container">
            <span
              v-for="tag in topTags"
              :key="tag"
              class="tag"
            >
              {{ tag }}
            </span>
          </div>
        </div>

        <!-- Unified Media Gallery -->
        <div v-if="mediaItems && mediaItems.length" class="media-section">
          <h3 class="section-title">Media</h3>
          <div class="media-gallery">
            <div 
              v-for="(item, index) in mediaItems.slice(0, mediaItems.length > 4 ? 3 : 4)" 
              :key="index" 
              class="media-item"
              @click.stop="openLightbox(index)"
            >
              <img 
                v-if="item.type === 'image'"
                :src="formatUrl(item.url)" 
                alt="Screenshot" 
                class="media-thumb"
                @error="handleImageError"
              />
              <div v-else class="video-thumb-container">
                <video 
                  :src="formatUrl(item.url)" 
                  class="media-thumb video-preview"
                  muted
                  preload="metadata"
                ></video>
                <div class="play-icon-overlay">▶</div>
              </div>
            </div>
            <div v-if="mediaItems.length > 4" class="more-media-badge" @click.stop="openLightbox(3)">
              +{{ mediaItems.length - 3 }}
            </div>
          </div>
        </div>

        <!-- Review Snippet -->
        <div v-if="game.review && game.review.review" class="review-section">
          <h3 class="section-title">Review Snippet</h3>
          <!-- Auto-expand if NOT a long review, otherwise follow state -->
          <div :class="['review-container', { expanded: reviewExpanded || !isLongReview }]">
            <p class="review-text" v-html="formattedReview"></p>
            <div v-if="isLongReview && !reviewExpanded" class="review-overlay"></div>
          </div>
          <button 
            v-if="isLongReview" 
            @click.stop="toggleReview" 
            class="spoiler-button"
          >
            {{ reviewExpanded ? 'Show Less' : 'Show Full Review' }}
          </button>
        </div>


      </div>
    </div>

    <!-- Enhanced Lightbox Overlay -->
    <div v-if="showLightbox" class="lightbox-overlay" @click="closeLightbox" @keydown.esc="closeLightbox" tabindex="0" ref="lightbox">
      <div class="lightbox-content" @click.stop>
        <button class="lightbox-close" @click="closeLightbox">×</button>
        
        <button class="lightbox-nav prev" @click.stop="prevMedia" v-if="mediaItems.length > 1">‹</button>
        
        <div class="lightbox-media-container">
          <img 
            v-if="currentMedia.type === 'image'" 
            :src="formatUrl(currentMedia.url)" 
            alt="Full size media" 
            class="lightbox-image" 
          />
          <video 
            v-else 
            :src="formatUrl(currentMedia.url)" 
            class="lightbox-video" 
            controls 
            autoplay
          ></video>
        </div>

        <button class="lightbox-nav next" @click.stop="nextMedia" v-if="mediaItems.length > 1">›</button>
        
        <div class="lightbox-counter">{{ currentMediaIndex + 1 }} / {{ mediaItems.length }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { secureUrl } from '@/utils/secure-url.js';

export default {
  props: {
    game: {
      type: Object,
      required: true
    },
    rank: {
      type: Number,
      default: null
    }
  },
  computed: {
    highlightedName() {
      return this.highlightText(this.game.name || 'Unknown Game');
    },
    computedDescription() {
      const desc = this.game.short_description || 'No description available.';
      if (this.descriptionExpanded) {
        return this.highlightText(desc);
      }
      const text = desc.length > 200 ? desc.substring(0, 200) + '...' : desc;
      return this.highlightText(text);
    },
    isDescriptionLong() {
      return (this.game.short_description || '').length > 200;
    },
    hasHltbData() {
        const hltb = this.game.how_long_to_beat;
        if (!hltb) return false;
        return (hltb.main_story > 0 || hltb.main_extra > 0 || hltb.completionist > 0);
    },
    lastSearchQuery() {
      return this.$store.state.lastSearchQuery;
    },
    hasReviews() {
      return (this.game.positive_reviews_count || 0) + (this.game.negative_reviews_count || 0) > 0;
    },
    reviewRatio() {
      const positive = this.game.positive_reviews_count || 0;
      const negative = this.game.negative_reviews_count || 0;
      const total = positive + negative;
      if (total === 0) return 'No reviews';
      const percentage = Math.round((positive / total) * 100);
      return `${percentage}% positive`;
    },
    topTags() {
      return this.game.tags ? this.game.tags.slice(0, 12) : [];
    },
    displayReasons() {
      return Array.isArray(this.game.reasons) ? this.game.reasons.slice(0, 4) : [];
    },
    hasScoreMath() {
      return Boolean(
        this.game.score_formula ||
        (this.game.score_breakdown && Object.keys(this.game.score_breakdown).length)
      );
    },
    scoreMathItems() {
      const breakdown = this.game.score_breakdown || {};
      const order = [
        ['final', 'Final', 'Combined ranking result after weighting'],
        ['semantic', 'Semantic', 'Similarity in meaning to your query'],
        ['metadata', 'Metadata', 'Overlap with genres, tags, and filters'],
        ['feature', 'Feature', 'Match on gameplay-style features'],
        ['temporal', 'Temporal', 'Fit for release-year or classic/retro intent'],
        ['text', 'Text', 'Title and lexical text match'],
        ['authority', 'Authority', 'Confidence from reviews and popularity'],
        ['purity', 'Purity', 'How strongly the game fits the core query facet'],
        ['quality', 'Quality', 'Broad quality boost for pillar-style results'],
        ['comparison_family', 'Comparison Family', 'Boost for same creator or close comparison family'],
        ['comparison_penalty', 'Comparison Penalty', 'Penalty for weak low-confidence comparison matches'],
        ['anime_pref', 'Anime Preference', 'Boost from the anime preference toggle'],
        ['adult_pref', '18+ Preference', 'Boost from the adult-content preference toggle'],
        ['vr_pref', 'VR Preference', 'Boost from the VR preference toggle'],
        ['anti_combat', 'Anti Combat', 'Boost for clearly non-combat games'],
        ['narrative', 'Narrative', 'Boost for story-rich or narrative-heavy games'],
        ['masterpiece', 'Masterpiece', 'Legacy boost signal'],
        ['series_penalty', 'Series Penalty', 'Reduces near-duplicate franchise results']
      ];
      return order
        .filter(([key]) => {
          const value = breakdown[key];
          if (value === null || value === undefined) return false;
          if (key === 'final') return true;
          return Math.abs(Number(value) || 0) >= 0.001;
        })
        .map(([key, label, description]) => ({
          label,
          description,
          value: this.formatDebugNumber(breakdown[key])
        }));
    },
    mediaItems() {
      const items = [];
      // Add videos first
      if (this.game.videos) {
        this.game.videos.forEach(v => items.push({ type: 'video', url: v.url }));
      }
      // Add screenshots
      if (this.game.screenshots) {
        this.game.screenshots.forEach(s => items.push({ type: 'image', url: s.url }));
      }
      return items;
    },
    currentMedia() {
      if (this.currentMediaIndex >= 0 && this.currentMediaIndex < this.mediaItems.length) {
        return this.mediaItems[this.currentMediaIndex];
      }
      return { type: 'image', url: '' };
    },
    primaryPrice() {
      if (this.game.is_free) return { amount: 0, currency: 'USD', is_currently_discounted: false };
      return this.game.prices && this.game.prices.length > 0 ? this.game.prices[0] : null;
    },
    displayPriceText() {
        if (!this.primaryPrice) return 'Check Store';
        return this.formatPrice(this.primaryPrice.discounted_amount || this.primaryPrice.amount, this.primaryPrice.currency);
    },
    storeLinks() {
      const links = this.game.links ? [...this.game.links] : [];
      
      // Check if we need to auto-generate Steam links
      const steamLinkIdx = links.findIndex(l => l.url && l.url.includes('store.steampowered.com'));
      
      if (this.game.app_id) {
          if (steamLinkIdx === -1) {
             links.unshift({
                store: 'Steam',
                url: `https://store.steampowered.com/app/${this.game.app_id}/`,
                icon: '' // No icon needed here, handled in template
             });
          } else {
              // Ensure the existing link is labeled simply "Steam"
              links[steamLinkIdx].store = 'Steam';
              links[steamLinkIdx].icon = ''; 
          }
      }

      // Process other links for icons - cleaned up for new template logic
      links.forEach(link => {
        if (!link.store) return;
        const s = link.store.toLowerCase();
        if (s === 'gog' || s.includes('gog')) {
            link.store = 'GOG';
        } else if (s === 'epic' || s.includes('epic')) {
            link.store = 'Epic';
        }
      });
      return links;
    },
    formattedReview() {
      if (!this.game.review || !this.game.review.review) return '';
      let text = this.game.review.review;
      
      // Basic BBCode parsing
      // Headers
      text = text.replace(/\[h1\](.*?)\[\/h1\]/gi, '<h3>$1</h3>');
      text = text.replace(/\[h2\](.*?)\[\/h2\]/gi, '<h4>$1</h4>');
      text = text.replace(/\[h3\](.*?)\[\/h3\]/gi, '<h5>$1</h5>');
      
      // Text styling
      text = text.replace(/\[b\](.*?)\[\/b\]/gi, '<strong>$1</strong>');
      text = text.replace(/\[i\](.*?)\[\/i\]/gi, '<em>$1</em>');
      text = text.replace(/\[u\](.*?)\[\/u\]/gi, '<u>$1</u>');
      text = text.replace(/\[strike\](.*?)\[\/strike\]/gi, '<del>$1</del>');
      text = text.replace(/\[spoiler\](.*?)\[\/spoiler\]/gi, '<span class="spoiler">$1</span>');
      text = text.replace(/\[url=(.*?)\](.*?)\[\/url\]/gi, '<a href="$1" target="_blank">$2</a>');
      
      // Lists
      text = text.replace(/\[list\]/gi, '<ul>');
      text = text.replace(/\[\/list\]/gi, '</ul>');
      text = text.replace(/\[\*\]/gi, '<li>');
      
      return text;
    },
    isLongReview() {
        if (!this.game.review || !this.game.review.review) return false;
        const text = this.game.review.review;
        // Check length (> 150) OR line breaks (> 3 lines)
        return text.length > 150 || text.split('\n').length > 3;
    }
  },
  data() {
    return {
      showLightbox: false,
      currentMediaIndex: 0,
      reviewExpanded: false,
      descriptionExpanded: false
    };
  },
  methods: {
    highlightText(text) {
      if (!text) return '';
      if (!this.lastSearchQuery) return text;

      // Extract terms, ignore small words unless they are "VR"
      const terms = this.lastSearchQuery.split(/\s+/).filter(t => t.length > 2 || t.toUpperCase() === 'VR');
      
      if (terms.length === 0) return text;

      // Escape regex special characters
      const escapedTerms = terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      
      // Create regex for case-insensitive matching
      const pattern = new RegExp(`(${escapedTerms.join('|')})`, 'gi');
      
      // Wrap matches in <mark>
      return text.replace(pattern, '<mark>$1</mark>');
    },
    toggleReview() {
        this.reviewExpanded = !this.reviewExpanded;
    },
    toggleDescription() {
        this.descriptionExpanded = !this.descriptionExpanded;
    },
    handleLinkClick(link) {
        this.logLinkClick();
        
        // Smart Steam Launch (User Request: Try App -> Fallback to Web)
        if (link.store === 'Steam' && this.game.app_id) {
            const steamProtocol = `steam://store/${this.game.app_id}`;
            const webUrl = link.url; 
            
            // 1. Try Protocol Launch
            window.location.href = steamProtocol;
            
            // 2. Fallback to Web if App didn't take focus (likely not installed or browser blocked)
            setTimeout(() => {
                // If the user switched away (document.hidden), we assume success and don't open the tab.
                // If they are still looking at the page, we open the web store.
                if (!document.hidden && !document.webkitHidden) {
                     window.open(webUrl, '_blank');
                }
            }, 500);
            return;
        }

        if (link.url.startsWith('steam://')) {
            window.location.href = link.url;
        } else {
            window.open(link.url, '_blank');
        }
    },
    logLinkClick() {
      this.$store.dispatch('logGameClick', this.game.id);
    },
    handleFeedback(feedback) {
      this.$store.dispatch('logFeedback', { gameId: this.game.id, feedback });
    },
    addTagToSearch(tag) {
      this.$store.commit('appendToUserInput', tag);
    },
    handleImageError(event) {
      event.target.style.display = 'none';
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    formatPrice(amount, currency) {
      if (amount === 0 || amount === '0') return 'Free';
      if (!amount || amount === 'N/A') return 'Check Store';
      
      try {
        const val = Number(amount);
        if (isNaN(val)) throw new Error('Invalid number');
        
        // Ensure currency is valid (fallback to USD to prevents crash)
        const validCurrency = (currency && currency.length === 3) ? currency : 'USD';

        return new Intl.NumberFormat(undefined, { 
          style: 'currency', 
          currency: validCurrency 
        }).format(val);
      } catch (error) {
        // Ultimate fallback
        return `${currency || '$'} ${amount}`;
      }
    },
    formatUrl(url) {
      return secureUrl(url);
    },
    formatHltbTime(hours) {
      if (!hours) return '-';
      // Fix floating point errors
      if (hours < 1) {
          // For very short games (e.g. 0.5 hours), show minutes? Or just 0.5h
          return Math.round(hours * 60) + 'm';
      }
      // For standard games, round to nearest integer usually looks cleanest, 
      // but 1 decimal is good for precision if < 10h
      if (hours < 10) {
          return parseFloat(hours.toFixed(1)) + 'h'; 
        }
      return Math.round(hours) + 'h';
    },
    formatDebugNumber(value) {
      if (value === null || value === undefined || value === '') return '-';
      const num = Number(value);
      if (Number.isNaN(num)) return '-';
      return num.toFixed(3);
    },
    openLightbox(index) {
      this.currentMediaIndex = index;
      this.showLightbox = true;
      document.body.style.overflow = 'hidden'; 
      this.$nextTick(() => {
        if (this.$refs.lightbox) this.$refs.lightbox.focus();
      });
    },
    closeLightbox() {
      this.showLightbox = false;
      document.body.style.overflow = '';
    },
    nextMedia() {
      if (this.currentMediaIndex < this.mediaItems.length - 1) {
        this.currentMediaIndex++;
      } else {
        this.currentMediaIndex = 0; // Loop back
      }
    },
    prevMedia() {
      if (this.currentMediaIndex > 0) {
        this.currentMediaIndex--;
      } else {
        this.currentMediaIndex = this.mediaItems.length - 1; // Loop to end
      }
    },
    handleKeydown(e) {
      if (!this.showLightbox) return;
      if (e.key === 'ArrowRight') this.nextMedia();
      if (e.key === 'ArrowLeft') this.prevMedia();
      if (e.key === 'Escape') this.closeLightbox();
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown);
  }
};
</script>

<style scoped>
.game-card-wrapper {
  /* maintain layout stability */
  margin-bottom: 24px;
}

/* Highlight Styles */
:deep(mark) {
  background-color: rgba(0, 220, 130, 0.2);
  color: #00dc82;
  font-weight: 700;
  border-radius: 2px;
  padding: 0 2px;
}

.game-card {
  position: relative;
  background: #161618; /* Cool Gray to counteract green perception */
  border: 1px solid #27272a;
  border-radius: 4px;
  overflow: hidden;
  transition: border-color 0.2s ease-out; /* Only animate border color, no transform/shadow */
}

/* NO GLOW, NO LIFT */
.game-card-wrapper:hover .game-card {
  border-color: #00dc82; 
}

.rank-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: #f59e0b;
  color: black;
  padding: 6px 12px;
  border-radius: 2px;
  font-weight: 800;
  font-size: 1.1rem;
  z-index: 10;
  box-shadow: 0 4px 0 #92400e; /* Hard shadow */
  border: 2px solid #000;
}

.card-header {
  display: flex;
  gap: 24px;
  padding: 24px;
  border-bottom: 1px solid #27272a;
  background: #111113; /* Deep Cool Gray */
}

.game-cover {
  width: 180px;
  height: 240px;
  object-fit: cover;
  border-radius: 2px;
  flex-shrink: 0;
  box-shadow: 0 4px 8px rgba(0,0,0,0.5);
  border: 1px solid #27272a;
}

.game-cover-placeholder {
  width: 180px;
  height: 240px;
  background: #27272a;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.placeholder-icon {
  width: 64px;
  height: 64px;
  color: #52525b;
}

.header-info {
  flex: 1;
  min-width: 0;
}

.game-title {
  font-size: 2rem;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 8px;
  line-height: 1.1;
  text-transform: uppercase;
  letter-spacing: -0.5px;
}

.game-description {
  color: #a1a1aa;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 8px;
}

.read-more-btn {
  background: transparent;
  border: none;
  color: #00dc82;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0;
  margin-bottom: 16px;
  letter-spacing: 0.5px;
}

.read-more-btn:hover {
  text-decoration: underline;
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.why-section {
  margin: -4px 0 18px;
}

.why-title {
  margin: 0 0 10px;
  color: #a1a1aa;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.why-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.score-math-panel {
  margin: 0 0 18px;
  border: 1px solid #2a2a2d;
  background: #121214;
  border-radius: 4px;
}

.score-math-panel summary {
  cursor: pointer;
  list-style: none;
  padding: 12px 14px;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #d4d4d8;
}

.score-math-panel summary::-webkit-details-marker {
  display: none;
}

.score-math-caption {
  margin: 0;
  padding: 0 14px 8px;
  color: #a1a1aa;
  font-size: 0.82rem;
  line-height: 1.45;
}

.score-formula {
  margin: 0;
  padding: 0 14px 12px;
  color: #d4d4d8;
  font-size: 0.88rem;
  line-height: 1.45;
}

.score-math-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
  padding: 0 14px 14px;
}

.score-math-item {
  border: 1px solid #27272a;
  background: #18181b;
  border-radius: 4px;
  padding: 10px;
}

.score-math-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #a1a1aa;
  margin-bottom: 4px;
}

.score-math-value {
  display: block;
  font-size: 0.95rem;
  font-weight: 800;
  color: #fafafa;
}

.score-math-description {
  display: block;
  margin-top: 4px;
  font-size: 0.76rem;
  line-height: 1.35;
  color: #a1a1aa;
}

.why-chip {
  display: inline-flex;
  align-items: center;
  padding: 7px 11px;
  border-radius: 999px;
  border: 1px solid rgba(0, 220, 130, 0.28);
  background: rgba(0, 220, 130, 0.08);
  color: #d7fff0;
  font-size: 0.86rem;
  font-weight: 600;
  line-height: 1.2;
}

.debug-panel {
  margin: 0 0 18px;
  padding: 10px 12px;
  border: 1px dashed #3f3f46;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.02);
}

.debug-panel summary {
  cursor: pointer;
  color: #a1a1aa;
  font-size: 0.82rem;
  font-weight: 700;
  user-select: none;
}

.debug-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(92px, 1fr));
  gap: 10px;
  margin-top: 10px;
}

.debug-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.debug-label {
  color: #71717a;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.debug-value {
  color: #f4f4f5;
  font-size: 0.92rem;
  font-variant-numeric: tabular-nums;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #a1a1aa;
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
}

.meta-item.score {
  color: #f59e0b;
  font-weight: 800;
}

.meta-icon {
  width: 18px;
  height: 18px;
}

.price-badge {
  background: #27272a;
  padding: 4px 8px;
  border-radius: 2px;
  color: #e4e4e7;
}

.price-badge.discounted {
  background: #7f1d1d;
  color: #fca5a5;
  border: 1px solid #ef4444;
}

.discount-percent {
  margin-left: 6px;
  background: #ef4444;
  color: white;
  padding: 0 4px;
  border-radius: 2px;
  font-size: 0.8em;
}

.feedback-section {
  display: flex;
  gap: 12px;
  align-items: center;
}

.feedback-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 2px;
  font-weight: 700;
  font-size: 0.9rem;
  border: 1px solid #3f3f46;
  background-color: #27272a;
  color: #e4e4e7;
  cursor: pointer;
  transition: all 0.1s ease-in-out;
  text-transform: uppercase;
}

.feedback-button .icon {
  width: 18px;
  height: 18px;
}

.feedback-button:hover {
  transform: translateY(-1px);
  background: #3f3f46;
}

.like-button:hover {
  border-color: #00dc82;
  color: #00dc82;
  background: rgba(0, 220, 130, 0.1);
}

.dislike-button:hover {
  border-color: #ef4444;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.card-body {
  padding: 24px;
}

.links-section-top {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
}

.store-link-compact {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #27272a;
  border: 1px solid #3f3f46;
  border-radius: 2px;
  color: #e4e4e7;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.2s ease;
  text-transform: uppercase;
}

.store-link-compact:hover {
  background: #3f3f46;
  border-color: #e4e4e7;
  color: white;
  transform: translateY(-1px);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #27272a;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  color: #71717a;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  color: #e4e4e7;
  font-size: 0.95rem;
  font-weight: 500;
}

.tags-section {
  margin-bottom: 24px;
}

.section-title {
  color: #00dc82;
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  background: #27272a;
  color: #a1a1aa;
  padding: 4px 10px;
  border-radius: 2px;
  font-size: 0.8rem;
  font-weight: 600;
  border: 1px solid #3f3f46;
  text-transform: uppercase;
  user-select: none;
}

.genre-tag {
  background: #064e3b;
  border-color: #059669;
  color: #34d399;
}

.hltb-section {
  margin-bottom: 24px;
}

.hltb-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 12px;
}

.hltb-card {
  background: #18181b;
  border: 1px solid #3f3f46;
  border-radius: 2px;
  padding: 12px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: all 0.2s ease;
}

.hltb-card:hover {
  border-color: #00dc82; /* Green instead of blue */
}

.hltb-label {
  font-size: 0.75rem;
  color: #34d399; /* Green tint */
  text-transform: uppercase;
  font-weight: 700;
}

.hltb-value {
  color: #ffffff;
  font-weight: 800;
  font-size: 1.25rem;
}

.screenshots-section {
  margin-bottom: 24px;
}

.screenshots-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.screenshot-img {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 2px;
  transition: transform 0.2s ease;
  border: 1px solid #27272a;
}

.screenshot-img:hover {
  transform: scale(1.02);
  border-color: #00dc82;
}

.clickable-image {
  cursor: zoom-in;
}

.videos-section {
  margin-bottom: 24px;
}

.videos-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 12px;
}

.video-player {
  width: 100%;
  max-height: 200px;
  border-radius: 2px;
  background: #000;
  border: 1px solid #27272a;
}

.review-section {
  margin-bottom: 24px;
  background: #121215;
  border: 1px solid #27272a;
  border-radius: 2px;
  padding: 16px;
}

.review-container {
  position: relative;
  max-height: 100px; /* Reduced collapsed height */
  overflow: hidden;
  transition: max-height 0.3s ease-out;
}

.review-container.expanded {
  max-height: none;
  overflow: visible;
}

.review-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background: linear-gradient(to bottom, transparent, #121215);
  pointer-events: none;
}

.review-text {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #d1d5db;
  margin: 0;
}

.spoiler-button {
  display: block;
  margin-top: 12px;
  background: transparent;
  border: 1px solid #3f3f46;
  color: #a1a1aa;
  padding: 6px 12px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
}

.spoiler-button:hover {
  background: #27272a;
  color: white;
}

/* Lightbox */
.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.lightbox-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.lightbox-image {
  max-width: 100%;
  max-height: 90vh;
  border-radius: 2px;
  box-shadow: 0 0 40px rgba(0, 0, 0, 0.5);
  border: 1px solid #3f3f46;
}

.lightbox-close {
  position: absolute;
  top: -40px;
  right: -10px;
  background: transparent;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .meta-row {
    justify-content: center;
  }

  .info-grid {
    text-align: left;
  }
}
/* Media Gallery */
.media-section {
  padding-bottom: 24px;
  margin-bottom: 24px;
  border-bottom: 1px solid #27272a;
}

.media-gallery {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-top: 12px;
}

.media-item {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  height: auto;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #27272a;
  transition: all 0.2s ease;
}

.media-item:hover {
  border-color: #00dc82;
  transform: scale(1.02);
}

.media-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-thumb-container {
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
}

.play-icon-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  background: rgba(0, 0, 0, 0.6);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  border: 1px solid white;
}

.more-media-badge {
  width: 100%;
  height: 100%;
  background: #27272a;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  flex-shrink: 0;
}

.more-media-badge:hover {
  background: #3f3f46;
}

/* Lightbox */
.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
}

.lightbox-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lightbox-media-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.lightbox-image,
.lightbox-video {
  max-width: 90vw;
  max-height: 85vh;
  border-radius: 4px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.8);
}

.lightbox-close {
  position: absolute;
  top: -40px;
  right: -20px;
  background: none;
  border: none;
  color: #fff;
  font-size: 32px;
  cursor: pointer;
  z-index: 10;
}

.lightbox-nav {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  font-size: 32px;
  padding: 20px 10px;
  cursor: pointer;
  position: fixed; /* Fixed to screen */
  top: 50%;
  transform: translateY(-50%);
  z-index: 10000; /* Ensure high z-index */
  border-radius: 4px;
  transition: background 0.2s;
}

.lightbox-nav:hover {
  background: rgba(0, 220, 130, 0.5);
}

.lightbox-nav.prev {
  left: 40px; /* Fixed distance from screen edge */
}

.lightbox-nav.next {
  right: 40px; /* Fixed distance from screen edge */
}

.lightbox-counter {
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  color: #cbd5e1;
  font-size: 0.9rem;
}

.collapse-btn {
  position: absolute;
  top: 8px;
  right: 8px;

  width: 28px;
  height: 28px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(24, 24, 27, 0.9);
  border: 1px solid #3f3f46;
  border-radius: 6px;

  color: #a1a1aa;
  font-size: 16px;
  line-height: 1;

  cursor: pointer;

  backdrop-filter: blur(6px);

  opacity: 0.85;

  transition: opacity 0.15s ease, transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;
}

.collapse-btn:hover {
  background: #18181b;
  color: #ffffff;
  border-color: #00dc82;
  opacity: 1;
  transform: scale(1.05);
}

.collapse-btn:active {
  transform: scale(0.95);
}

@media (max-width: 768px) {
  .lightbox-nav {
    position: fixed;
    bottom: 20px;
    top: auto;
    transform: none;
    padding: 10px 20px;
  }
  
  .lightbox-nav.prev {
    left: 20px;
  }
  
  .lightbox-nav.next {
    right: 20px;
  }
  
  .lightbox-close {
    top: 10px;
    right: 10px;
    position: fixed;
  }
}

@media (max-width: 640px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .game-cover, .game-cover-placeholder {
    width: 100%;
    height: 200px; /* Banner style */
    object-position: top center;
  }

  /* Adjust rank badge position since cover is now full width */
  .rank-badge {
    top: 8px;
    left: 8px;
    font-size: 0.9rem;
    padding: 4px 8px;
  }

  .game-title {
    font-size: 1.5rem;
  }
  
  .meta-row {
    gap: 12px;
  }

  .collapse-btn {
    top: 6px;
    right: 6px;
    width: 24px;
    height: 24px;
    font-size: 14px;
  }
}
</style>
